import {Component, OnInit, ViewChild} from '@angular/core';
import {Subject, Subscription} from "rxjs";
import {DataTableDirective} from "angular-datatables";
import {UserDataStoreService} from "../services/user-data-store.service";
import {AuthenticationService} from "../services/authentication.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import { Router } from '@angular/router';
import {TerminalConfigService} from "../services/terminal-config.service";
import {TerminalConfigViewModel} from "../models/terminal-config-view-model";
import { ToastrService } from 'ngx-toastr';
import { PermissionEnum } from '../services/guards/permissions';
import {ConfirmationDialogComponent} from "../partials/confirmation-dialog/confirmation-dialog.component";
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { SearchFilterModel } from '../models/search-filter-model';

@Component({
  selector: 'app-terminal-config-management',
  templateUrl: './terminal-config-management.component.html',
  styleUrls: ['./terminal-config-management.component.scss']
})
export class TerminalConfigManagement implements OnInit {
  public dtOptions: DataTables.Settings = {};
  public terminalConfigs: TerminalConfigViewModel[] = [];
  public dtTrigger: Subject<boolean> = new Subject();

  public canView: boolean = false;
  public canEdit: boolean = false;
  public canAdd: boolean = false;
  public canDelete: boolean = false;
  public AddButton: string;
  public EditButton: string;
  public DeleteButton: string;
  public EditView: string;

  public filter: SearchFilterModel = {
    search: '',
    take: 10,
    skip: 0,
    orderBy: null,
    orderDirection: 'ASC'
  };

  @ViewChild(DataTableDirective)
  public dtElement: DataTableDirective;


  constructor(
    private _router: Router,
    private _userDataStore: UserDataStoreService,
    private _authenticationService: AuthenticationService,
    private _modalService: NgbModal,
    private _terminalConfigService: TerminalConfigService,
    private _toastService: ToastrService,
    private _ngxUiLoaderService: NgxUiLoaderService) {


    this.canView = this._userDataStore.hasRequiredPermissions([PermissionEnum.ViewTerminalConfiguration], []);
    if (!this.canView) {
      this._toastService.warning("You do not have the required permissions to view this page, Please contact your system administrator", "Warning");
      this._router.navigate(['/']);
    }

    this.canAdd = this._userDataStore.hasRequiredPermissions([PermissionEnum.AddTerminalConfiguration], []);
    if (!this.canAdd) {
      this.AddButton = "display: none";
    }

    this.canEdit = this._userDataStore.hasRequiredPermissions([PermissionEnum.EditTerminalConfiguration], []);
    if (!this.canEdit) {
      if (this.canView) {
        this.EditView = "View";
      }
    }
    else {
      this.EditView = "Edit";
    }

    this.canDelete = this._userDataStore.hasRequiredPermissions([PermissionEnum.DeleteTerminalConfiguration], []);
    if (!this.canDelete) {
      this.DeleteButton = "display: none";
    }


  }

  ngOnInit() {
    this.dtOptions = {
      pageLength: 10,
      pagingType: 'full_numbers',
      serverSide: true,
      processing: true,
      ajax: async (dataTablesParameters: any, callback) => {
        this.filter.search = dataTablesParameters.search.value;
        this.filter.take = dataTablesParameters.length;
        this.filter.skip = (dataTablesParameters.start / dataTablesParameters.length) * dataTablesParameters.length;
        this.filter.orderDirection = dataTablesParameters.order[0].dir;
        this.filter.orderBy = dataTablesParameters.columns[dataTablesParameters.order[0].column].data;
        const result = await this._terminalConfigService.GetPagedConfigs(this.filter);
        this.terminalConfigs = result.response;
        callback({
          recordsTotal: result.total,
          recordsFiltered: result.total,
          data: []
        });
      },
      responsive: true,
      columns: [
        { name: 'Terminal Config', data: 'Id' },
        { name: 'Name', data: 'Name' },
        { name:'Actions', data: 'actions', orderable: false }
      ]
    };
  }

  public async ngAfterViewInit(): Promise<void> {
    await this.init();
  }

  private async init(): Promise<void> {
    this.dtTrigger.next();
  }

  private resetDatatable(): void {
    this.filter.skip = 0;
    this.filter.take = 0;
    this.terminalConfigs = [];
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next();
    });
  }

  public async addTerminalConfig(): Promise<void>{
    this._router.navigate(['management', 'terminal-config', 'new']);
  }

  public async duplicateTerminalConfig(terminal: TerminalConfigViewModel): Promise<void>{

    this.canAdd = this._userDataStore.hasRequiredPermissions([PermissionEnum.AddTerminalConfiguration], []);
    if (!this.canAdd) {
      this._toastService.error("You do not have the required permissions to add a terminal config record.", "error");
    } else {
      terminal.name = `${terminal.name.split('-copy')[0]}-copy(${terminal.id + 1})`;
      terminal.id = 1;

      const createResult = await this._terminalConfigService.Add(terminal);
      if (createResult.success === true) {
        this._toastService.success("Terminal config duplicated", "Success");
        this.terminalConfigs = await this._terminalConfigService.GetAllConfigs();
      } else {
        this._toastService.error(createResult.message, "Error");
      }
    }
  }

  public async editTerminalConfig(terminal: TerminalConfigViewModel): Promise<void> {
    this._router.navigate(['management', 'terminal-config', terminal.id]);
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  public async openDuplicationConfirmationDialog(terminal: TerminalConfigViewModel) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Terminal Config Duplication"
    dialogRef.componentInstance.message = "Are you sure you want to duplicate the selected terminal config?"

    const result = await dialogRef.result;
    if (result) {
      await this.duplicateTerminalConfig(terminal);
    }
  }

  public async deleteTerminalConfig(config: TerminalConfigViewModel): Promise<void> {
    this.canDelete = this._userDataStore.hasRequiredPermissions([PermissionEnum.DeleteTerminalConfiguration], []);
    if (!this.canDelete) {
      this._toastService.error("You do not have the required permissions to delete a terminal config record.", "error");
    }
    else {
      const result = await this._terminalConfigService.DeleteTerminalConfig(config.id);
      if (result.success === true) {
        this._toastService.success(result.message, "Success");
        this.terminalConfigs = await this._terminalConfigService.GetAllConfigs();
      } else {
        this._toastService.error(result.message, "Error");
      }
    }
  }

  public async determineDeleteDialog(config: TerminalConfigViewModel) {
    var terminalDetails = await this._terminalConfigService.GetAllTerminalDetails(config.id);
    if (!!terminalDetails && terminalDetails.length === 0) {
      await this.openDeleteConfirmationDialog(config);
    } else {
      await this.openUsedDeleteFailureConfirmationDialog(terminalDetails.map(td => 'TID: ' + td.terminalId));
    }
  }

  public async openDeleteConfirmationDialog(terminal: TerminalConfigViewModel) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.showCancel = true;
    dialogRef.componentInstance.acceptButtonText = "Yes";
    dialogRef.componentInstance.cancelButtonText = "No"
    dialogRef.componentInstance.title = "Terminal config removal"
    dialogRef.componentInstance.message = "Are you sure you want to delete the selected terminal config?"

    const result = await dialogRef.result;
    if (result) {
      await this.deleteTerminalConfig(terminal);
    }
  }

  public async openUsedDeleteFailureConfirmationDialog(terminals: string[]) {
    const dialogRef = this._modalService.open(ConfirmationDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    dialogRef.componentInstance.acceptButtonText = "OK"
    dialogRef.componentInstance.title = "Terminal config cannot be removed"
    dialogRef.componentInstance.message = "This Terminal Config is currently in use and cannot be deleted.<br/><br/><b>Terminals using config:</b>"
    dialogRef.componentInstance.listMessage = terminals;
    dialogRef.componentInstance.listMessageHtml;
  }
}
